// Tela da partida: HUD, treinadores, sinergias, tabuleiro (PixiJS), loja,
// componentes, painel da unidade, escolhas (insígnia, Eevee, Safari) e fim.
import { computeActiveTraits } from "@shared/traits";
import { buzz, play } from "../audio/sfx";
import { sellValue } from "@shared/shop";
import type { PlayerState, UnitInstance } from "@shared/match/types";
import type { Cost } from "@shared/types";
import { BoardRenderer } from "../game/board";
import { GameStore, gameStore, setGameStore } from "../game/store";
import { abilityText, badgeName, badgeOption, componentName, itemIcon, itemName, set, speciesFor, traitName, unitDef, unitItemSlots, unitName } from "../data";
import { forgetRoom, rejoinMatch, setCurrentRoom } from "../net";
import { navigate, type View } from "../router";
import { spriteHtml, loadSprites } from "../sprites";
import { esc, soundButton } from "../ui";
import { mountRoomChat } from "../roomchat";
import { t, type MsgKey } from "../i18n";
import { currentUser, loadSession } from "../session";
import { reportButton } from "../report";
import { shareResult } from "../sharecard";
import { startCoach } from "../coach";

const ROUND_KINDS = ["pve", "pvp", "safari", "gym"];
const GAME_ERRORS = ["not-enough-gold", "bench-full", "board-full", "max-level", "items-full", "not-planning", "rate-limited"];

function tierClass(active: { tier: number; def: { breakpoints: number[] } }): string {
  if (active.tier < 0) return "";
  const last = active.def.breakpoints.length - 1;
  return active.tier >= last && last > 0 ? "gold" : active.tier >= 1 ? "silver" : "bronze";
}

export const matchView: View = (root) => {
  const store = gameStore();
  if (!store) {
    navigate("/menu");
    return;
  }
  let selected: string | null = null;
  let selectedComponent: number | null = null;
  let lastRoundKey = "";
  let combatLabel = "";

  root.innerHTML = `
    <div class="game__layout bw" data-game>
      <header class="bw-hud game__hud">
        <span class="bw-hud__item bw-coin" data-gold>0</span>
        <span class="bw-hud__item" data-level>${t("match.level", { n: 1 })}</span>
        <span class="bw-hud__item badges" data-badges title="${t("match.badges")}"></span>
        <div class="bw-hp bw-hp--mana bw-xp" data-xp style="--hp:0"><span class="bw-hp__label">XP</span><div class="bw-hp__track"><div class="bw-hp__fill"></div></div></div>
        <span class="bw-hud__item game__round" data-round></span>
        <span class="bw-hud__item game__timeline" data-timeline></span>
        <span class="bw-timer" data-timer>--</span>
        <button class="bw-btn bw-btn--square" data-chat-toggle aria-label="${t("social.roomChat")}">💬<span class="social-badge" data-chat-badge hidden></span></button>
        ${soundButton()}
        <button class="bw-btn bw-btn--square game__theme" data-quit aria-label="${t("match.quit")}">✕</button>
      </header>
      <aside class="game__players"><details class="game__drawer" open><summary class="game__drawer-title">${t("match.trainers")}</summary><div class="bw-players" data-players></div></details></aside>
      <main class="game__center"><div class="game-board" data-board></div></main>
      <aside class="game__traits"><details class="game__drawer" open><summary class="game__drawer-title">${t("match.traits")}</summary><div class="bw-traits" data-traits></div></details></aside>
      <footer class="game__shop" data-sell-zone>
        <div class="game__shop-actions">
          <button class="bw-btn bw-btn--blue" data-reroll>↻ 2</button>
          <button class="bw-btn bw-btn--green" data-xpbtn>XP 4</button>
          <button class="bw-btn bw-btn--square" data-lock aria-pressed="false" aria-label="${t("match.lockShop")}">🔒</button>
          <button class="bw-btn bw-btn--red fight-btn" data-ready hidden>⚔ ${t("tut.fightBtn")}</button>
        </div>
        <div class="shop-col">
          <div class="bw-shop" data-shop></div>
          <div class="components" data-components></div>
        </div>
      </footer>
    </div>
    <div class="unit-panel bw-panel" data-panel hidden></div>
    <div class="modal-layer" data-modal hidden></div>
    <div class="toast" data-toast hidden></div>
    <section class="bw-panel match-chat" data-chat-panel hidden><div data-chat></div></section>`;

  const $ = <T extends HTMLElement>(s: string) => root.querySelector<T>(s)!;
  if (matchMedia("(orientation: portrait)").matches) root.querySelectorAll("details").forEach((d) => d.removeAttribute("open"));

  // ---------------------------------------------------------------- toast
  let toastTimer = 0;
  const toast = (msg: string) => {
    const el = $("[data-toast]");
    el.textContent = msg;
    el.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => (el.hidden = true), 2200);
  };

  // -------------------------------------------------------------- tabuleiro
  const board = new BoardRenderer($("[data-board]"), {
    onMove: (from, to) => store.send({ type: "move", from, to }),
    onSell: (uid) => store.send({ type: "sell", uid }),
    onSelect: (uid) => {
      if (uid && selectedComponent !== null) {
        store.send({ type: "equip", component: selectedComponent, uid });
        selectedComponent = null;
        renderComponents(store.view?.me ?? null);
        return;
      }
      selected = uid;
      board.setSelected(uid);
      renderPanel();
    },
  });

  // ---------------------------------------------------------------- render
  const findUnit = (me: PlayerState, uid: string): UnitInstance | undefined =>
    [...me.board, ...me.bench].find((u): u is UnitInstance => !!u && u.uid === uid);

  function renderHud() {
    const v = store!.view;
    if (!v?.me) return;
    const me = v.me;
    $("[data-gold]").textContent = String(me.gold);
    $("[data-level]").textContent = t("match.level", { n: me.level });
    $("[data-badges]").innerHTML = me.badges.length
      ? me.badges.map((b) => {
          const [id, opt] = b.split(":");
          const def = set.badges.find((x) => x.id === id);
          return `<i class="badge-icon mini" title="${esc(badgeName(id ?? "", def?.name))}: ${esc(badgeOption(id ?? "", Number(opt), def?.options[Number(opt)]))}"></i>`;
        }).join("")
      : "";
    const need = set.rules.xpToNext[String(me.level)];
    $("[data-xp]").style.setProperty("--hp", need ? String(me.xp / need) : "1");
    $("[data-round]").textContent = v.roomPhase === "combat" && combatLabel ? combatLabel : `${v.stage}-${v.round} · ${ROUND_KINDS.includes(v.roundKind) ? t(`match.round.${v.roundKind}` as MsgKey) : ""}`;
    
    const roundList = v.stage === 1 ? set.rounds.stage1 : set.rounds.default;
    $("[data-timeline]").innerHTML = roundList.map((rk: string, idx: number) => {
      const rNum = idx + 1;
      const isPast = rNum < v.round;
      const isCurrent = rNum === v.round;
      const opacity = isPast ? 0.3 : isCurrent ? 1 : 0.6;
      const color = isCurrent ? "var(--color-gold, #ffd700)" : "inherit";
      const icon = rk === "pve" ? "fa-solid fa-paw" :
                   rk === "pvp" ? "fa-solid fa-khanda" :
                   rk === "safari" ? "fa-solid fa-gift" :
                   rk === "gym" ? "fa-solid fa-medal" : "fa-solid fa-circle";
      return `<i class="${icon}" style="opacity: ${opacity}; color: ${color}; margin-right: 4px;" title="${ROUND_KINDS.includes(rk) ? t(`match.round.${rk}` as MsgKey) : rk}"></i>`;
    }).join("");

    // sozinho (solo/tutorial): "Lutar!" começa o combate sem esperar o tempo
    $("[data-ready]").hidden = !(v.roomPhase === "planning" && v.players.filter((p) => p.alive).length === 1);
    $<HTMLButtonElement>("[data-reroll]").textContent = me.freeRerolls > 0 ? `↻ ${t("match.free", { n: me.freeRerolls })}` : `↻ ${set.rules.rerollCost}`;
    $<HTMLButtonElement>("[data-xpbtn]").textContent = `XP ${set.rules.xpBuyCost}`;
    $("[data-lock]").setAttribute("aria-pressed", String(me.shopLocked));
  }

  function renderPlayers() {
    const v = store!.view;
    if (!v) return;
    const sorted = [...v.players].sort((a, b) => Number(b.alive) - Number(a.alive) || b.hp - a.hp);
    $("[data-players]").innerHTML = sorted
      .map((p) => {
        const lvl = p.hp > 50 ? "high" : p.hp > 20 ? "mid" : "low";
        // foto de perfil; sem foto, o primeiro Pokémon do tabuleiro
        const ace = p.board.find(Boolean);
        const species = p.avatar ?? (ace ? speciesFor(ace.unitId, ace.star, ace.branch) : 25);
        return `<div class="bw-player" ${p.id === store!.myId ? 'data-self="true"' : ""} ${p.alive ? "" : 'data-dead="true"'}>
          ${spriteHtml(species, 32)}
          <div class="bw-hp" data-level="${lvl}" style="--hp:${Math.max(0, p.hp) / 100}">
            <span class="bw-hp__label" title="${esc(p.nickname)}">${esc(p.nickname)}</span>
            <div class="bw-hp__track"><div class="bw-hp__fill"></div></div>
          </div>
          <span class="player-hp">${p.alive ? p.hp : `${p.placement}<sup class="ord">º</sup>`}</span>
        </div>`;
      })
      .join("");
  }

  function renderTraits() {
    const me = store!.view?.me;
    if (!me) return;
    const refs = me.board.filter((u): u is UnitInstance => !!u).map((u) => ({ unitId: u.unitId, branch: u.branch }));
    const active = computeActiveTraits(refs, set);
    $("[data-traits]").innerHTML = active.length
      ? active
          .map((tr) => {
            const tier = tierClass(tr);
            const bps = tr.def.breakpoints.map((b) => (tr.count >= b ? `<b>${b}</b>` : String(b))).join(" › ");
            return `<div class="bw-trait" ${tier ? `data-active="true" data-tier="${tier}"` : ""} data-tip="trait:${tr.key}:${tr.count}">
              <span class="bw-trait__icon">${tr.count}</span><span>${esc(traitName(tr.key))}</span><span class="bw-trait__bps">${bps}</span></div>`;
          })
          .join("")
      : `<p class="bw-dim small">${t("match.traits.empty")}</p>`;
  }

  function renderShop() {
    const me = store!.view?.me;
    if (!me) return;
    // cópias que o jogador já tem (★1 = 1, ★2 = 3, ★3 = 9), para brilhar na loja
    const copies = new Map<string, number>();
    for (const u of [...me.board, ...me.bench]) if (u) copies.set(u.unitId, (copies.get(u.unitId) ?? 0) + (u.star === 1 ? 1 : u.star === 2 ? 3 : 9));
    $("[data-shop]").innerHTML = me.shop
      .map((id, slot) => {
        if (!id) return `<button class="bw-shop-slot" data-empty="true" disabled></button>`;
        const def = unitDef(id)!;
        const have = copies.get(id) ?? 0;
        return `<button class="bw-shop-slot ${have ? "shop-owned" : ""}" data-cost="${def.cost}" data-slot="${slot}" data-tip="unit:${id}:${have}" ${have ? 'data-owned="true"' : ""} ${me.gold < def.cost ? 'aria-disabled="true"' : ""}>
          ${have ? `<span class="owned-count">${have % 3 === 2 ? "★2!" : `${have % 3}/3`}</span>` : ""}
          <span class="bw-shop-slot__cost bw-coin">${def.cost}</span>
          ${spriteHtml(speciesFor(id, 1), 56)}
          <span><span class="bw-shop-slot__name">${esc(unitName(id, 1))}</span>
          <span class="bw-shop-slot__traits">${def.traits.map((k) => `<span class="bw-type" data-type="${k}">${esc(traitName(k))}</span>`).join("")}</span></span>
        </button>`;
      })
      .join("");
  }

  function renderComponents(me: PlayerState | null) {
    if (!me) return;
    $("[data-components]").innerHTML = me.components.length
      ? me.components
          .map((c, i) => `<button class="comp ${selectedComponent === i ? "on" : ""}" data-comp="${i}" data-tip="item:${c}" aria-label="${esc(componentName(c))}"><img src="${itemIcon(c)}" alt="" width="30" height="30"></button>`)
          .join("")
      : "";
  }

  function renderPanel() {
    const panel = $("[data-panel]");
    const me = store!.view?.me;
    const u = me && selected ? findUnit(me, selected) : undefined;
    if (!u || !me) {
      panel.hidden = true;
      return;
    }
    const def = unitDef(u.unitId)!;
    const branch = u.branch ? def.branches?.find((b) => b.id === u.branch) : undefined;
    const ability = branch?.ability ?? def.ability;
    const ab = abilityText(ability);
    const traits = [...def.traits, ...(branch?.addTraits ?? [])];
    panel.hidden = false;
    panel.innerHTML = `
      <div class="unit-panel__head">${spriteHtml(speciesFor(u.unitId, u.star, u.branch), 64)}
        <div><b>${esc(unitName(u.unitId, u.star, u.branch))}</b> <span class="stars s${u.star}">${"★".repeat(u.star)}</span><br>
        ${traits.map((k) => `<span class="bw-type" data-type="${k}">${esc(traitName(k))}</span>`).join(" ")}
        <span class="bw-dim small">· ${esc(traitName(branch?.class ?? def.class))} · ${t("match.range", { n: branch?.range ?? def.range })}</span></div>
        <button class="bw-btn bw-btn--square" data-close aria-label="${t("common.close")}">✕</button></div>
      <p class="small"><b>${esc(ab.name)}</b> (${t("match.mana")} ${ability.mana[0]}/${ability.mana[1]}): ${esc(ab.desc)}</p>
      ${u.items.length ? `<div class="unit-items">${unitItemSlots(u.items).map((it) => `<span class="item-chip" data-tip="item:${it.id}"><img src="${itemIcon(it.id)}" alt="" width="24" height="24">${esc(itemName(it.id))}</span>`).join("")}</div>` : ""}
      <div class="bw-row">
        <button class="bw-btn bw-btn--red" data-sell>${t("match.sell", { n: sellValue(def.cost as Cost, u.star) })}</button>
        ${me.components.map((c, i) => `<button class="bw-btn equip-btn" data-equip="${i}" data-tip="item:${c}"><img src="${itemIcon(c)}" alt="" width="24" height="24">${t("match.equip")}</button>`).join("")}
      </div>`;
    panel.querySelector("[data-close]")!.addEventListener("click", () => {
      selected = null;
      board.setSelected(null);
      renderPanel();
    });
    panel.querySelector("[data-sell]")!.addEventListener("click", () => {
      store!.send({ type: "sell", uid: u.uid });
      selected = null;
      board.setSelected(null);
      panel.hidden = true;
    });
    panel.querySelectorAll<HTMLElement>("[data-equip]").forEach((b) =>
      b.addEventListener("click", () => store!.send({ type: "equip", component: Number(b.dataset.equip), uid: u.uid })),
    );
  }

  // --------------------------------------------------------------- modais
  function renderModal() {
    const v = store!.view;
    const me = v?.me;
    const modal = $("[data-modal]");
    if (!v || !me || v.roomPhase === "combat") {
      modal.hidden = true;
      return;
    }
    let html = "";
    if (me.pendingBadge) {
      const badge = set.badges.find((b) => b.id === me.pendingBadge!.badgeId)!;
      html = `<div class="bw-panel modal"><div class="bw-panel__header">${esc(badgeName(badge.id, badge.name))} · ${esc(badge.leader)}</div>
        <div class="bw-textbox">${t("match.badge.choose")}</div>
        <div class="badge-options">${me.pendingBadge.options.map((o) => `<button class="bw-btn badge-opt" data-badge="${o}"><i class="badge-icon"></i><b>${esc(badgeOption(badge.id, o, badge.options[o]))}</b></button>`).join("")}</div></div>`;
    } else if (me.pendingBranch.length) {
      const uid = me.pendingBranch[0]!;
      const u = findUnit(me, uid);
      const def = u && unitDef(u.unitId);
      if (u && def?.branches) {
        html = `<div class="bw-panel modal"><div class="bw-panel__header">${t("match.branch.choose")}</div>
          <div class="badge-options">${def.branches
            .map((b) => `<button class="bw-btn badge-opt" data-branch="${b.id}" data-uid="${uid}">${spriteHtml(b.species, 64)}<b>${esc(unitName(u.unitId, 2, b.id))}</b>
              <span class="small">+${b.addTraits.map(traitName).join(", ")} · ${esc(traitName(b.class))}</span></button>`)
            .join("")}</div></div>`;
      }
    } else if (me.pendingComponentChoice) {
      html = `<div class="bw-panel modal"><div class="bw-panel__header">${t("match.backpack")}</div>
        <div class="badge-options">${Object.keys(set.components)
          .map((c) => `<button class="bw-btn badge-opt" data-component="${c}" data-tip="item:${c}"><img src="${itemIcon(c)}" alt="" width="32" height="32"><b>${esc(componentName(c))}</b></button>`)
          .join("")}</div></div>`;
    } else if (v.roundKind === "safari" && v.safari?.length) {
      html = `<div class="bw-panel modal"><div class="bw-panel__header">Safari Zone</div>
        <div class="bw-textbox">${t("match.safari.text")}</div>
        <div class="badge-options">${v.safari
          .map((o, i) => `<button class="bw-btn badge-opt ${me.safariPick === i ? "bw-btn--blue" : ""}" data-safari="${i}">${spriteHtml(speciesFor(o.unitId, 1), 56)}<b>${esc(unitName(o.unitId, 1))}</b><span class="small" data-tip="item:${o.component}"><img src="${itemIcon(o.component)}" alt="" width="20" height="20"> ${esc(componentName(o.component))}</span></button>`)
          .join("")}</div>
        ${me.safariPick !== null ? `<p class="bw-dim small center">${t("match.safari.picked")}</p>` : ""}</div>`;
    }
    modal.hidden = !html;
    modal.innerHTML = html;
  }

  $("[data-modal]").addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>("button");
    if (!btn) return;
    if (btn.dataset.badge) store.send({ type: "chooseBadge", option: Number(btn.dataset.badge) });
    if (btn.dataset.branch) store.send({ type: "chooseBranch", uid: btn.dataset.uid!, branch: btn.dataset.branch });
    if (btn.dataset.component) store.send({ type: "chooseComponent", component: btn.dataset.component });
    if (btn.dataset.safari) store.send({ type: "safariPick", index: Number(btn.dataset.safari) });
  });

  // ------------------------------------------------------------- ações HTML
  $("[data-shop]").addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>("[data-slot]");
    if (b) store.send({ type: "buy", slot: Number(b.dataset.slot) });
  });
  $("[data-components]").addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>("[data-comp]");
    if (!b) return;
    const i = Number(b.dataset.comp);
    selectedComponent = selectedComponent === i ? null : i;
    if (selectedComponent !== null) toast(t("match.equipHint"));
    renderComponents(store.view?.me ?? null);
  });
  $("[data-reroll]").addEventListener("click", () => store.send({ type: "reroll" }));
  $("[data-xpbtn]").addEventListener("click", () => store.send({ type: "buyXp" }));
  $("[data-lock]").addEventListener("click", () => store.send({ type: "lockShop" }));
  $("[data-ready]").addEventListener("click", () => store.ready());
  const stopCoach = store.lobby?.mode === "tutorial" ? startCoach(store, root) : () => {};
  $("[data-quit]").addEventListener("click", async () => {
    if (store.ended || confirmQuit()) await leave();
  });

  // atalhos de teclado (PC)
  const onKey = (e: KeyboardEvent) => {
    if ((e.target as HTMLElement)?.tagName === "INPUT") return;
    if (e.key === "d" || e.key === "D") store.send({ type: "reroll" });
    if (e.key === "f" || e.key === "F") store.send({ type: "buyXp" });
    if ((e.key === "e" || e.key === "E") && selected) store.send({ type: "sell", uid: selected });
  };
  window.addEventListener("keydown", onKey);

  function confirmQuit(): boolean {
    const modal = $("[data-modal]");
    const me = store?.view?.me;
    const isDead = me && !me.alive;
    modal.hidden = false;
    modal.innerHTML = `<div class="bw-panel modal"><div class="bw-panel__header">${t("match.quit.title")}</div>
      <div class="bw-textbox">${t("match.quit.text")}</div>
      <div class="bw-row"><button class="bw-btn bw-btn--red" data-yes>${t("match.quit.yes")}</button><button class="bw-btn" data-no>${t("match.quit.no")}</button></div></div>`;
    modal.querySelector("[data-yes]")!.addEventListener("click", () => void leave(!!isDead));
    modal.querySelector("[data-no]")!.addEventListener("click", () => renderModal());
    return false;
  }

  async function leave(final = true) {
    // sair sem "consentir" mantém a vaga para voltar; ao fim da partida sai de vez
    if (final) {
      // a sala pode já ter fechado (o servidor fecha 3 s depois do fim): não espera para sempre
      await Promise.race([store!.room.leave(true).catch(() => {}), new Promise((r) => setTimeout(r, 800))]);
      forgetRoom();
    } else {
      store!.room.connection.close(4999);
    }
    setCurrentRoom(null);
    setGameStore(null);
    navigate("/menu");
  }

  // ------------------------------------------------------------ fim/queda
  function renderEnd() {
    const e = store!.ended;
    if (!e) return;
    const mine = e.placements.find((p) => p.id === store!.myId);
    const modal = $("[data-modal]");
    modal.hidden = false;
    modal.innerHTML = `<div class="bw-panel modal end">
      <p class="placement">${mine?.placement ?? "-"}<sup class="ord">º</sup></p>
      <p class="bw-subtitle">${e.placements.length === 1 ? (store!.view?.me?.alive ? t("match.end.league") : t("match.end.journey")) : (mine?.placement ?? 9) <= 4 ? t("match.end.top4") : t("match.end.over")}</p>
      ${e.mode === "tutorial" ? `<p class="bw-alert bw-alert--ok">🎓 ${t("tut.done")}</p>` : ""}
      <ol class="end-list">${e.placements.map((p) => `<li ${p.id === store!.myId ? 'class="me"' : ""}><b>${p.placement}<sup class="ord">º</sup></b> ${esc(p.nickname)}${p.id !== store!.myId && p.userId && p.userId !== currentUser()?.id ? reportButton(p.userId, p.nickname) : ""}</li>`).join("")}</ol>
      ${e.reward ? `<div class="rewards">
        ${e.reward.ranked ? `<p class="lp-delta ${e.reward.ranked.pointsDelta < 0 ? "neg" : ""}">${t("match.end.points", { n: `${e.reward.ranked.pointsDelta > 0 ? "+" : ""}${e.reward.ranked.pointsDelta}` })}</p>` : ""}
        <p>${t("match.end.xp", { n: e.reward.xp })}${e.reward.levelUp ? ` · <b>${t("match.end.levelUp", { n: e.reward.level })}</b>` : ""}</p>
        <p class="pdx">${t("match.end.pokedollars", { n: e.reward.pokedollars })}</p>
        ${e.reward.missionsDone ? `<p><a href="#/missoes">${t("missions.doneInMatch", { n: e.reward.missionsDone })}</a></p>` : ""}
        ${e.reward.petUnlocked ? `<p><b>${t("creche.unlocked", { name: unitName(e.reward.petUnlocked, 1) })}</b></p>` : ""}
        ${e.reward.shiny ? `<p>${t("match.end.shiny")}</p>` : ""}
        ${(e.reward.achievements ?? []).map((id) => `<p class="ach-toast-line">🏅 <a href="#/conquistas">${esc(t("ach.toast", { name: t(`ach.${id}.name` as MsgKey) }))}</a></p>`).join("")}
      </div>` : ""}
      <div class="bw-row end-actions">
        ${e.mode !== "tutorial" && mine?.placement ? `<button class="bw-btn bw-btn--gold" data-share>📤 ${t("share.button")}</button>` : ""}
        <button class="bw-btn bw-btn--blue" data-menu>${t("match.end.menu")}</button>
      </div></div>`;
    modal.querySelector("[data-menu]")!.addEventListener("click", () => void leave(true));
    if (e.reward?.achievements?.length) {
      play("levelUp");
      void loadSession();
    }
    modal.querySelector("[data-share]")?.addEventListener("click", async (ev) => {
      const btn = ev.currentTarget as HTMLButtonElement;
      const v = store!.view;
      const me = v?.me;
      const u = currentUser();
      btn.setAttribute("aria-busy", "true");
      try {
        const how = await shareResult({
          nickname: u?.nickname ?? mine?.nickname ?? "", title: u?.title ?? null, avatar: u?.avatarSpecies ?? null,
          placement: mine!.placement!, players: e.placements.length, level: me?.level ?? 1, stage: v?.stage ?? 1, round: v?.round ?? 1,
          board: (me?.board ?? []).filter((x): x is UnitInstance => !!x),
          shiny: v?.players.find((p) => p.id === store!.myId)?.shiny ?? [],
          pvpWins: (me as { track?: { pvpWins?: number } } | undefined)?.track?.pvpWins, pokedollars: e.reward?.pokedollars,
        });
        if (how === "downloaded") toast(t("share.downloaded"));
      } catch {
        toast(t("common.error"));
      } finally {
        btn.removeAttribute("aria-busy");
      }
    });
  }

  let reconnecting = false;
  async function handleDisconnect() {
    if (reconnecting || store!.ended) return;
    reconnecting = true;
    toast(t(store!.migrating ? "match.migrating" : "match.reconnecting"));
    const room = await rejoinMatch(store!.matchId ?? undefined);
    if (room) {
      setCurrentRoom(room);
      setGameStore(new GameStore(room));
      navigate("/partida");
      return;
    }
    toast(t("match.reconnectFailed"));
  }

  // ------------------------------------------------------------ assinatura
  let lastLevel = 0;
  const render = () => {
    const v = store.view;
    if (store.disconnected) void handleDisconnect();
    if (store.lastError && GAME_ERRORS.includes(store.lastError)) {
      toast(t(`match.err.${store.lastError}` as MsgKey));
      store.lastError = null;
    }
    if (!v?.me) return;
    if (lastLevel && v.me.level > lastLevel) {
      play("levelUp");
      buzz(30);
    }
    lastLevel = v.me.level;
    const mePub = v.players.find((p) => p.id === store.myId);
    board.setShinies(mePub?.shiny ?? []);
    if (v.roomPhase !== "combat") {
      void board.setArena(mePub?.arena ?? null);
      board.setPlanning(v.me);
    }
    if (selected && !findUnit(v.me, selected)) {
      selected = null;
      board.setSelected(null);
    }
    renderHud();
    renderPlayers();
    renderTraits();
    renderShop();
    renderComponents(v.me);
    renderPanel();
    if (store.ended) renderEnd();
    else renderModal();
    const key = `${v.stage}-${v.round}`;
    if (key !== lastRoundKey && v.roomPhase === "planning") {
      lastRoundKey = key;
      if (v.roundKind === "gym") {
        const badge = set.badges.find((b) => b.round === key);
        toast(badge ? t("match.gym.toast", { leader: badge.leader, badge: badgeName(badge.id, badge.name) }) : t("match.round.gym"));
      } else if (v.roundKind === "safari") toast(t("match.safari.toast"));
    }
  };

  const unsubRound = store.onRound((r) => {
    const fight = r.events.find((ev) => ev.type === "pvp" && (ev.player === store.myId || ev.data?.vs === store.myId));
    const oppId = fight ? (fight.player === store.myId ? fight.data?.vs : fight.player) : null;
    const players = store.view?.players ?? [];
    board.setShinies(players.find((p) => p.id === store.myId)?.shiny ?? [], players.find((p) => p.id === oppId)?.shiny ?? []);
    void board.setArena(r.arena ?? null);
    if (r.combat?.length && r.side) {
      void board.playCombat(r.combat, r.side);
      // no fim da animação: som de vitória/derrota da rodada
      const mineFight = r.events.find((ev) => (ev.type === "pvp" || ev.type === "pve") && (ev.player === store.myId || ev.data?.vs === store.myId));
      const winner = mineFight?.data?.winner ?? (mineFight?.type === "pve" ? (mineFight.data?.won ? "a" : "b") : null);
      if (winner) {
        const won = winner === r.side;
        const endT = (r.combat[r.combat.length - 1] as { t?: number }).t ?? 0;
        setTimeout(() => {
          play(won ? "win" : "lose");
          if (!won) buzz(80);
        }, Math.min(15_000, endT * 50 + 200));
      }
    }
    else board.setPlanning(store.view?.me ?? null);
    selected = null;
    $("[data-panel]").hidden = true;
    $("[data-modal]").hidden = true;
    const mine = r.events.find((ev) => (ev.type === "pvp" || ev.type === "pve") && (ev.player === store.myId || ev.data?.vs === store.myId));
    const vs = mine?.type === "pve" ? t("match.vs.wild") : mine?.data?.npc ? t("match.vs.npc")
      : mine?.data?.async ? t("match.vs.ghost", { name: String(mine.data.nickname ?? "?") }) : store.view?.players.find((p) => p.id === (mine?.player === store.myId ? mine?.data?.vs : mine?.player))?.nickname;
    combatLabel = vs ? t("match.combatVs", { name: vs }) : t("match.combat");
    $("[data-round]").textContent = combatLabel;
  });

  let lastTick = -1;
  const timer = setInterval(() => {
    const d = store.view?.deadline;
    const el = $("[data-timer]");
    if (!d) return (el.textContent = "--");
    const s = Math.max(0, Math.ceil((d - Date.now()) / 1000));
    el.textContent = String(s).padStart(2, "0");
    if (s <= 5 && s > 0 && store.view?.roomPhase === "planning" && s !== lastTick) play("tick");
    lastTick = s;
    el.dataset.urgent = String(s <= 5 && store.view?.roomPhase === "planning");
  }, 200);

  // chat da partida: painel recolhível; mensagem nova acende o contador
  let unreadChat = 0;
  const chatPanel = $("[data-chat-panel]");
  const chatBadge = $("[data-chat-badge]");
  const unsubChat = mountRoomChat($("[data-chat]"), store, {
    compact: true,
    onMessage: (m) => {
      if (chatPanel.hidden && m.from !== store.myId) {
        unreadChat++;
        chatBadge.textContent = String(unreadChat);
        chatBadge.hidden = false;
        toast(`${m.nickname}: ${m.text}`);
      }
    },
  });
  $("[data-chat-toggle]").addEventListener("click", () => {
    chatPanel.hidden = !chatPanel.hidden;
    unreadChat = 0;
    chatBadge.hidden = true;
    if (!chatPanel.hidden) chatPanel.querySelector<HTMLInputElement>("input")?.focus();
  });

  const unsub = store.subscribe(render);
  void loadSprites().then(render);

  return () => {
    stopCoach();
    unsub();
    unsubChat();
    unsubRound();
    clearInterval(timer);
    window.removeEventListener("keydown", onKey);
    board.destroy();
  };
};
